import React from "react";
import { useNavigate } from "react-router-dom";
import { MdAdd, MdArrowBack, MdDelete, MdEdit } from "react-icons/md";
import Card from "components/card";
import { reportTemplateService } from "services/admin/reportTemplateService";
import type { ReportTemplate } from "types/domain";
import { useLanguage } from "context/LanguageContext";
import ReportTemplateForm from "./ReportTemplateForm";

const fieldTypeBadge = (type: string) => {
  if (type === "TEXT")
    return "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400";
  if (type === "NUMBER")
    return "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400";
  return "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400";
};

const ReportTemplateList: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [templates, setTemplates] = React.useState<ReportTemplate[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [showForm, setShowForm] = React.useState(false);
  const [editingTemplate, setEditingTemplate] =
    React.useState<ReportTemplate | null>(null);

  const loadTemplates = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await reportTemplateService.getAll();
      setTemplates(data);
    } catch (err: any) {
      setError(err.message ?? "reportTemplate.loadFailed");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void loadTemplates();
  }, [loadTemplates]);

  const handleDelete = async (id: string) => {
    if (!window.confirm(t("reportTemplate.confirmDelete"))) return;
    try {
      await reportTemplateService.delete(id);
      await loadTemplates();
    } catch (err: any) {
      alert(err.message ?? t("reportTemplate.deleteFailed"));
    }
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingTemplate(null);
  };

  const handleFormSave = async () => {
    handleFormClose();
    await loadTemplates();
  };

  if (showForm || editingTemplate) {
    return (
      <ReportTemplateForm
        template={editingTemplate}
        onCancel={handleFormClose}
        onSave={handleFormSave}
      />
    );
  }

  return (
    <div className="mt-3">
      <Card extra="p-6">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/admin/report-templates")}
              className="rounded p-1.5 text-ink-muted hover:bg-surface-hover"
            >
              <MdArrowBack className="h-5 w-5" />
            </button>
            <h2 className="text-xl font-bold text-ink">
              {t("reportTemplate.listTitle")}
            </h2>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600"
          >
            <MdAdd className="h-5 w-5" />
            {t("reportTemplate.create")}
          </button>
        </div>

        {loading && <p className="text-ink-muted">{t("state.loading")}</p>}
        {error && <p className="text-red-500">{t(error)}</p>}

        {!loading && templates.length === 0 && (
          <p className="text-ink-muted">{t("reportTemplate.empty")}</p>
        )}

        <div className="grid gap-4">
          {templates.map((template) => (
            <div
              key={template.id}
              className="rounded-lg border border-line p-4"
            >
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-ink">
                    {template.name}
                    {template.is_default && (
                      <span className="ml-2 inline-block rounded bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400">
                        {t("reportTemplate.default")}
                      </span>
                    )}
                  </h3>
                  {template.description && (
                    <p className="mt-1 text-sm text-ink-muted">
                      {template.description}
                    </p>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setEditingTemplate(template)}
                    className="rounded p-1.5 text-ink-muted hover:bg-surface-hover"
                  >
                    <MdEdit className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(template.id)}
                    className="rounded p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                  >
                    <MdDelete className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {template.fields.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {template.fields
                    .sort((a, b) => a.sort_order - b.sort_order)
                    .map((field) => (
                      <span
                        key={field.id}
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${fieldTypeBadge(
                          field.field_type
                        )}`}
                      >
                        {field.label}
                        {field.is_required && (
                          <span className="ml-1 text-red-500">*</span>
                        )}
                        <span className="ml-1 opacity-60">
                          ({field.field_type})
                        </span>
                      </span>
                    ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

export default ReportTemplateList;
